import { TrezorException } from '@injectivelabs/exceptions';
import { addHexPrefix, publicKeyToAddress } from '@injectivelabs/sdk-ts';
import { DEFAULT_NUM_ADDRESSES_TO_FETCH } from '@injectivelabs/wallet-base';
import { loadTrezorConnect } from '../lib.js';
import { TrezorDerivationPathType } from '../../types.js';
const addressOfHDKey = (hdKey) => {
    const shouldSanitizePublicKey = true;
    const derivedPublicKey = hdKey.publicKey;
    const ethereumAddressWithoutPrefix = Buffer.from(publicKeyToAddress(derivedPublicKey, shouldSanitizePublicKey)).toString('hex');
    const address = addHexPrefix(ethereumAddressWithoutPrefix);
    return address;
};
export default class AccountManager {
    wallets = [];
    constructor() {
        this.wallets = [];
    }
    async getWallets(baseDerivationPath, derivationPathType) {
        const { start, end } = this.getOffset();
        /**
         * 1. Wallets are not yet fetched at all,
         * 2. Wallets are not yet fetched for that offset
         */
        if (!this.hasWallets() || !this.hasWalletsInOffset(start)) {
            await this.getWalletsBasedOnIndex({
                start,
                end,
                baseDerivationPath,
                derivationPathType,
            });
        }
        return this.wallets.slice(start, end);
    }
    getTrezorDerivationPathBasedOnType = ({ fullBaseDerivationPath, derivationPathType, index, }) => {
        if (derivationPathType === TrezorDerivationPathType.Bip44) {
            return `${fullBaseDerivationPath}/${index}'/0/0`;
        }
        if (derivationPathType === TrezorDerivationPathType.Legacy) {
            return `m/${index}`;
        }
        return `${fullBaseDerivationPath}/0'/0/${index}`;
    };
    async getWalletsBasedOnIndex({ start, end, baseDerivationPath, derivationPathType, }) {
        const TrezorConnect = await loadTrezorConnect();
        const pathsToFetch = [];
        for (let index = start; index < end; index += 1) {
            const path = this.getTrezorDerivationPathBasedOnType({
                fullBaseDerivationPath: baseDerivationPath,
                derivationPathType,
                index,
            });
            pathsToFetch.push({
                path,
                showOnTrezor: false,
            });
        }
        const result = await TrezorConnect.ethereumGetPublicKey({
            bundle: pathsToFetch,
        });
        if (!result.success) {
            throw new TrezorException(new Error((result.payload && result.payload.error) ||
                'Please make sure your Trezor is connected and unlocked'));
        }
        for (const item of result.payload) {
            const hdKey = {
                publicKey: Buffer.from(item.publicKey, 'hex'),
                chainCode: Buffer.from(item.chainCode, 'hex'),
            };
            const address = addressOfHDKey(hdKey);
            this.wallets.push({
                hdKey,
                derivationPath: item.serializedPath,
                address: address.toLowerCase(),
            });
        }
    }
    hasWallets() {
        return this.wallets.length > 0;
    }
    hasWalletsInOffset(offset) {
        return this.wallets.length > offset;
    }
    getOffset() {
        const totalWallets = this.wallets.length;
        const nextBatchStart = totalWallets;
        const nextBatchEnd = totalWallets + DEFAULT_NUM_ADDRESSES_TO_FETCH;
        return {
            start: nextBatchStart,
            end: nextBatchEnd,
        };
    }
    hasWalletForAddress(address) {
        return (this.wallets.find((wallet) => wallet.address.toLowerCase() === address.toLowerCase()) !== undefined);
    }
    async getWalletForAddress(address) {
        return this.wallets.find((wallet) => wallet.address.toLowerCase() === address.toLowerCase());
    }
    reset() {
        this.wallets = [];
    }
}
